/**
 * A per-account SAVED COPY of the slow-moving lists the app reads on every
 * load — the card definitions, element sets and collections (KPR-1080).
 *
 * The in-memory caches (CardDefinitionsContext, CollectionsContext) reset on a
 * full page load, so a reload paints empty cards and an empty rail until the
 * fetches land. A context seeds itself from this copy, shows it at once, and
 * ALWAYS refetches in the background and replaces it (stale-while-revalidate;
 * with conditional reads, KPR-1079, an unchanged list costs a 304). The copy
 * is a speed-up and never an authority:
 *
 * - it is read only for the signed-in account it was written for (the key
 *   carries the user id), and removed whenever the session changes — sign-out,
 *   another account, a 401 — and, at load, when it belongs to someone else;
 * - it carries a schema version and a written-at time: a copy from another
 *   version, or older than MAX_AGE_MS, is ignored and removed;
 * - a copy that does not parse, is too big to keep (MAX_BYTES, so one account
 *   never fills storage) or cannot be written — storage blocked, full, a
 *   private window — is ignored without an error: the app behaves as if there
 *   were none.
 *
 * What it holds is what the account was already allowed to read; it is cleared
 * with the session like every other per-account store (docs/ARCHITECTURE.md).
 */
import { getSessionUserId, onSessionChange } from './session';

/** Bump when the shape of a stored list changes, so an old copy is dropped. */
export const SAVED_COPY_VERSION = 1;
/** A copy this old is not shown; the background fetch is the first paint instead. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * One copy's serialized size ceiling (characters). Over it, nothing is kept.
 * A large account's card definitions are ~2.5 MB today (measured against the
 * live API, KPR-1080), so the ceiling clears that with room and still leaves
 * localStorage's ~5 MB for everything else; a write that does not fit is
 * dropped, never allowed to push out a draft.
 */
export const MAX_BYTES = 3 * 1024 * 1024;

const PREFIX = 'keepr.saved.';

const storage = () => {
	try {
		return window.localStorage;
	} catch {
		return null;
	}
};

const keyFor = (userId, name) => `${PREFIX}${userId}.${name}`;

const keysOf = store => {
	const keys = [];
	try {
		for (let i = 0; i < store.length; i += 1) {
			const key = store.key(i);
			if (key && key.startsWith(PREFIX)) keys.push(key);
		}
	} catch { /* unreadable: nothing to list */ }
	return keys;
};

const remove = (store, key) => {
	try { store.removeItem(key); } catch { /* already gone */ }
};

/** Remove every saved copy — or, with `keepUserId`, every one that is not that account's. */
export function clearSavedCopies({ keepUserId = null } = {}) {
	const store = storage();
	if (!store) return;
	keysOf(store).forEach(key => {
		if (keepUserId && key.startsWith(`${PREFIX}${keepUserId}.`)) return;
		remove(store, key);
	});
}

/**
 * The saved copy called `name` for the signed-in account, or null: none,
 * another account's, another version, too old, unreadable.
 */
export function readSavedCopy(name, now = Date.now()) {
	const userId = getSessionUserId();
	const store = storage();
	if (!userId || !store) return null;
	const key = keyFor(userId, name);
	try {
		const raw = store.getItem(key);
		if (raw === null) return null;
		const copy = JSON.parse(raw);
		const fresh = copy && copy.v === SAVED_COPY_VERSION && typeof copy.at === 'number' && now - copy.at >= 0 && now - copy.at <= MAX_AGE_MS;
		if (!fresh || copy.data === undefined) {
			remove(store, key);
			return null;
		}
		return copy.data;
	} catch {
		remove(store, key);
		return null;
	}
}

/** Keep `data` as the signed-in account's copy called `name`. Returns whether it was kept. */
export function writeSavedCopy(name, data, now = Date.now()) {
	const userId = getSessionUserId();
	const store = storage();
	if (!userId || !store) return false;
	const key = keyFor(userId, name);
	try {
		const raw = JSON.stringify({ v: SAVED_COPY_VERSION, at: now, data });
		if (raw.length > MAX_BYTES) {
			remove(store, key); // a copy too big to keep must not leave an older one standing
			return false;
		}
		store.setItem(key, raw);
		return true;
	} catch {
		remove(store, key); // full or blocked: no copy is better than a stale one
		return false;
	}
}

// The session ends or changes hands: nothing of the last account stays behind.
onSessionChange(userId => clearSavedCopies({ keepUserId: userId }));
// And at load: a copy left by an account that is not the one signed in now
// (a token that expired, then another person signing in) goes before anyone
// reads it. Reads are keyed by user id regardless; this keeps storage clean.
clearSavedCopies({ keepUserId: getSessionUserId() });
